import { Component, OnInit, OnChanges, DoCheck, Input, ViewChild, ChangeDetectorRef, SimpleChanges, KeyValueDiffers, KeyValueDiffer, IterableDiffers, IterableDiffer } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import {
  FilterState,
  ChartDataset,
  ChartColorConfig,
  ChartUpdateService,
  AnalyticsHttpService,
  AnalyticsExportService,
  getBarChartOptions,
  getStackedBarChartOptions,
  getLineChartOptions,
  getDoughnutChartOptions,
  ANALYTICS_COLORS,
  CHART_UPDATE_DELAY,
  sortStackedBarChartData,
  sortBarChartData,
  limitBarChartData,
  SortDirection,
  DEFAULT_CHART_ITEM_LIMIT,
  calculateFirstDatasetTotal,
  getDatasetValue,
  getArrayValue,
  calculateArrayTotal,
  getProductValue,
  getProductTotal,
  formatProductName,
  getBreakdownDisplayName as getBreakdownDisplayNameUtil,
  createConcentricDonutDatasets,
  createSingleDonutDataset,
  lightenColor,
  hexToRgb
} from 'app/core/analytics';

@Component({
  selector: 'kt-consumption-tab',
  templateUrl: './consumption-tab.component.html',
  styleUrls: [
    '../../analytics.component.scss',
    './consumption-tab.component.scss'
  ]
})
export class ConsumptionTabComponent implements OnInit, OnChanges, DoCheck {
  @Input() filters!: FilterState;
  @Input() drinkOptions?: Array<{ value: string | number; label: string }>;
  @Input() productOptions?: Array<{ value: string | number; label: string }>;
  @Input() distributorOptions?: Array<{ value: string | number; label: string }>;
  @Input() marketOptions?: Array<{ value: string | number; label: string }>;
  @Input() clientOptions?: Array<{ value: string | number; label: string }>;
  @Input() campusOptions?: Array<{ value: string | number; label: string }>;
  @Input() machineOptions?: Array<{ value: string | number; label: string }>;

  @ViewChild('consumptionBarChart', { static: false }) consumptionBarChart!: BaseChartDirective;
  @ViewChild('consumptionLineChart', { static: false }) consumptionLineChart!: BaseChartDirective;
  @ViewChild('productBreakdownDonutChart', { static: false }) productBreakdownDonutChart!: BaseChartDirective;

  public barChartOptions = getBarChartOptions();
  public stackedBarChartOptions = getStackedBarChartOptions();
  public lineChartOptions = getLineChartOptions();
  public productBreakdownDonutChartOptions = getDoughnutChartOptions(false);

  public barChartColors: ChartColorConfig[] = [];
  public donutChartColors: ChartColorConfig[] = [];
  public lineChartColors: ChartColorConfig[] = [];

  public consumptionBarChartLabels: string[] = [];
  public consumptionBarChartData: ChartDataset[] = [];
  public consumptionCompareDataArray: number[] = [];
  public consumptionBarChartSort: SortDirection = 'desc';
  public showAllConsumptionItems = false;

  private originalConsumptionBarChartLabels: string[] = [];
  private originalConsumptionBarChartData: ChartDataset[] = [];
  private originalConsumptionCompareDataArray: number[] = [];

  private fullConsumptionBarChartLabels: string[] = [];
  private fullConsumptionBarChartData: ChartDataset[] = [];
  private fullConsumptionCompareDataArray: number[] = [];

  public consumptionLineChartLabels: string[] = [];
  public consumptionLineChartData: ChartDataset[] = [];

  public productBreakdownDonutLabels: string[] = [];
  public productBreakdownDonutData: ChartDataset[] = [];
  public productBreakdownDonutChartKey: number = 1;

  public isLoadingConsumption = false;
  public isLoadingConsumptionOverTime = false;

  private isDestroyingCharts: boolean = false;
  private filtersDiffer: KeyValueDiffer<string, any> | null = null;
  private arrayDiffers: Map<string, IterableDiffer<string>> = new Map();

  // Mock contextual data mapping for tooltips
  private contextualDataMap: Map<string, { partner?: string; client?: string; campus?: string }> = new Map();

  // Custom tooltip state
  public tooltipVisible = false;
  public tooltipX = 0;
  public tooltipY = 0;
  public tooltipText = '';

  constructor(
    private analyticsHttpService: AnalyticsHttpService,
    private chartUpdateService: ChartUpdateService,
    private analyticsExportService: AnalyticsExportService,
    private cdr: ChangeDetectorRef,
    private keyValueDiffers: KeyValueDiffers,
    private iterableDiffers: IterableDiffers
  ) {
    this.initializeColors();
  }

  ngOnInit(): void {
    this.filtersDiffer = this.keyValueDiffers.find(this.filters).create();
    this.initializeArrayDiffers();
    this.loadAllData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.filtersDiffer = this.keyValueDiffers.find(this.filters).create();
      this.initializeArrayDiffers();
      this.loadAllData();
    }
  }

  ngDoCheck(): void {
    if (this.filtersDiffer) {
      const changes = this.filtersDiffer.diff(this.filters);
      if (changes) {
        this.loadAllData();
        return;
      }
    }

    const arrayKeys = ['drinks', 'distributors', 'markets', 'clients', 'campuses', 'machines', 'products'];
    for (const key of arrayKeys) {
      const differ = this.arrayDiffers.get(key);
      if (differ) {
        const changes = differ.diff(this.filters[key]);
        if (changes) {
          this.loadAllData();
          return;
        }
      }
    }
  }

  private initializeArrayDiffers(): void {
    const arrayKeys = ['drinks', 'distributors', 'markets', 'clients', 'campuses', 'machines', 'products'];
    for (const key of arrayKeys) {
      this.arrayDiffers.set(key, this.iterableDiffers.find(this.filters[key]).create());
    }
  }

  private initializeColors(): void {
    this.barChartColors = [{
      backgroundColor: ANALYTICS_COLORS.PRIMARY,
      borderColor: ANALYTICS_COLORS.PRIMARY
    }];

    this.donutChartColors = [{
      backgroundColor: Array.from(ANALYTICS_COLORS.PRODUCT),
      hoverBackgroundColor: Array.from(ANALYTICS_COLORS.PRODUCT),
      borderColor: '#ffffff',
      borderWidth: 2
    }];

    this.lineChartColors = [{
      borderColor: ANALYTICS_COLORS.PRODUCT[0],
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      pointBackgroundColor: ANALYTICS_COLORS.PRODUCT[0],
      pointBorderColor: ANALYTICS_COLORS.PRODUCT[0]
    }];
  }


  private loadAllData(): void {
    this.loadConsumptionData();
    this.loadConsumptionOverTimeData();
  }

  private loadData<T>(
    loadingFlagRef: () => boolean,
    setLoadingFlag: (value: boolean) => void,
    httpCall: (params: Record<string, unknown>) => Observable<T>,
    onSuccess: (response: T) => void,
    onError?: () => void,
    modifyParams?: (params: Record<string, unknown>) => void
  ): void {
    if (loadingFlagRef()) {
      return;
    }

    const queryParams = this.buildQueryParams();
    if (!queryParams) {
      return;
    }

    setLoadingFlag(true);

    if (modifyParams) {
      modifyParams(queryParams);
    }

    if (this.filters.compareEnabled) {
      queryParams.compareEnabled = 'true';
    }

    httpCall(queryParams).subscribe(
      (response: T) => {
        setLoadingFlag(false);
        onSuccess(response);
      },
      (error: unknown) => {
        setLoadingFlag(false);
        if (onError) {
          onError();
        }
      }
    );
  }

  private areCustomDatesReady(): boolean {
    if (this.filters.datePreset !== 'custom') {
      return true;
    }
    return !!this.filters.startDate && !!this.filters.endDate;
  }

  private buildQueryParams(): Record<string, unknown> | null {
    if (!this.areCustomDatesReady()) {
      return null;
    }

    const params: Record<string, unknown> = {
      breakdownBy: this.filters.breakdownBy,
      datePreset: this.filters.datePreset
    };

    if (this.filters.datePreset !== 'custom') {
      params.days = this.filters.days;
    } else {
      if (this.filters.startDate) {
        params.startDate = this.filters.startDate;
      }
      if (this.filters.endDate) {
        params.endDate = this.filters.endDate;
      }
    }

    if (this.filters.distributors.length > 0) {
      params.distributors = this.filters.distributors.join(',');
    }
    if (this.filters.markets.length > 0) {
      params.markets = this.filters.markets.join(',');
    }
    if (this.filters.clients.length > 0) {
      params.clients = this.filters.clients.join(',');
    }
    if (this.filters.campuses.length > 0) {
      params.campuses = this.filters.campuses.join(',');
    }
    if (this.filters.machines.length > 0) {
      params.machines = this.filters.machines.join(',');
    }
    if (this.filters.products.length > 0) {
      params.products = this.filters.products.join(',');
    }
    if (this.filters.drinks.length > 0) {
      params.drinks = this.filters.drinks.join(',');
    }

    if (this.filters.averageEnabled) {
      params.averageEnabled = 'true';
      params.avgBy = this.filters.avgBy;
    }

    return params;
  }


  loadConsumptionData(): void {
    this.loadData(
      () => this.isLoadingConsumption,
      (value) => { this.isLoadingConsumption = value; },
      (params) => this.analyticsHttpService.getConsumption(params),
      (response) => this.handleConsumptionResponse(response),
      () => {
        this.consumptionBarChartLabels = [];
        this.consumptionBarChartData = [{
          label: 'Current Period',
          data: [],
          borderWidth: 0,
          stack: 'current'
        }];
        this.productBreakdownDonutLabels = [];
        this.productBreakdownDonutData = [];
        this.productBreakdownDonutChartOptions = getDoughnutChartOptions(false);
        this.forceUpdateConsumptionChart();
        this.recreateProductBreakdownDonutChart();
      }
    );
  }

  private parseApiResponse(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const responseObj = response as Record<string, unknown>;
    const data = (responseObj.data && typeof responseObj.data === 'object' && !Array.isArray(responseObj.data))
      ? responseObj.data as Record<string, unknown>
      : responseObj;

    return data;
  }

  /**
   * Extract contextual data from API response
   */
  private extractContextualData(data: Record<string, unknown>): Record<string, { partner?: string; client?: string; campus?: string }> | undefined {
    const contextualData = data.contextualData;
    if (contextualData && typeof contextualData === 'object' && !Array.isArray(contextualData)) {
      return contextualData as Record<string, { partner?: string; client?: string; campus?: string }>;
    }
    return undefined;
  }

  private createSimpleBarChartDatasets(values: number[], compareData?: number[], formattedLabels?: string[]): ChartDataset[] {
    const currentBackgroundColor = new Array(values.length).fill(ANALYTICS_COLORS.PRIMARY);
    const currentBorderColor = new Array(values.length).fill(ANALYTICS_COLORS.PRIMARY);

    const currentDataset: ChartDataset = {
      label: 'Current Period',
      data: values.slice(),
      backgroundColor: currentBackgroundColor,
      borderColor: currentBorderColor,
      borderWidth: 0,
      stack: 'current'
    };

    const datasets: ChartDataset[] = [currentDataset];

    const hasCompareData = this.filters.compareEnabled &&
      compareData &&
      Array.isArray(compareData) &&
      compareData.length > 0;

    if (hasCompareData && compareData && formattedLabels) {
      const alignedCompareData = formattedLabels.map((_, index) => {
        if (!compareData || !Array.isArray(compareData)) {
          return 0;
        }
        return compareData[index] || 0;
      });
      const previousBackgroundColor = new Array(alignedCompareData.length).fill(ANALYTICS_COLORS.PREVIOUS);
      const previousBorderColor = new Array(alignedCompareData.length).fill(ANALYTICS_COLORS.PREVIOUS);

      datasets.push({
        label: 'Previous Period',
        data: alignedCompareData.slice(),
        backgroundColor: previousBackgroundColor,
        borderColor: previousBorderColor,
        borderWidth: 0,
        stack: 'previous'
      });
    }

    return datasets;
  }

  private handleConsumptionResponse(response: unknown): void {
    const data = this.parseApiResponse(response);
    if (!data) {
      this.consumptionBarChartLabels = [];
      this.consumptionBarChartData = [{
        label: 'Current Period',
        data: [],
        borderWidth: 0,
        stack: 'current'
      }];
      this.productBreakdownDonutLabels = [];
      this.productBreakdownDonutData = [];
      this.productBreakdownDonutChartOptions = getDoughnutChartOptions(false);
      this.forceUpdateConsumptionChart();
      this.recreateProductBreakdownDonutChart();
      return;
    }

    const isSimpleFormat = data.labels && Array.isArray(data.labels) &&
                          data.data && Array.isArray(data.data) &&
                          !data.datasets;

    if (isSimpleFormat) {
      this.handleSimpleConsumptionResponse(data);
    } else if (data.labels && Array.isArray(data.labels) &&
               data.datasets && Array.isArray(data.datasets)) {
      this.handleStackedConsumptionResponse(data);
    } else {
      this.consumptionBarChartLabels = [];
      this.consumptionBarChartData = [{
        label: 'Current Period',
        data: [],
        borderWidth: 0,
        stack: 'current'
      }];
      this.productBreakdownDonutLabels = [];
      this.productBreakdownDonutData = [];
      this.productBreakdownDonutChartOptions = getDoughnutChartOptions(false);
      this.forceUpdateConsumptionChart();
      this.recreateProductBreakdownDonutChart();
      return;
    }
  }

  private handleSimpleConsumptionResponse(data: Record<string, unknown>): void {
    const labels = (data.labels as string[]) || [];
    const values = (data.data as number[]) || [];
    const compareData = (data.compareData as number[]) || (data.compare as number[]) || undefined;

    const breakdownBy = this.filters.breakdownBy;
    const formattedLabels = breakdownBy === 'product'
      ? labels.map(label => formatProductName(label))
      : labels.slice();

    this.consumptionBarChartLabels = formattedLabels;

    // Populate contextual data from API response
    const contextualData = this.extractContextualData(data);
    if (contextualData) {
      this.populateContextualData(formattedLabels, contextualData);
    }

    const datasets = this.createSimpleBarChartDatasets(values, compareData, formattedLabels);
    this.consumptionBarChartData = datasets;

    const hasCompareData = this.filters.compareEnabled &&
      compareData &&
      Array.isArray(compareData) &&
      compareData.length > 0;

    if (hasCompareData && datasets.length > 1 && datasets[1].data) {
      this.consumptionCompareDataArray = (datasets[1].data as number[]).slice();
    } else {
      this.consumptionCompareDataArray = [];
    }

    this.originalConsumptionBarChartLabels = formattedLabels.slice();
    this.originalConsumptionBarChartData = datasets.map(dataset => ({
      ...dataset,
      data: (dataset.data as number[]).slice()
    }));
    this.originalConsumptionCompareDataArray = hasCompareData && this.consumptionCompareDataArray.length > 0
      ? this.consumptionCompareDataArray.slice()
      : [];

    this.sortConsumptionBarChart();

    const colorConfigs: ChartColorConfig[] = [{
      backgroundColor: ANALYTICS_COLORS.PRIMARY,
      borderColor: ANALYTICS_COLORS.PRIMARY
    }];

    if (hasCompareData) {
      colorConfigs.push({
        backgroundColor: ANALYTICS_COLORS.PREVIOUS,
        borderColor: ANALYTICS_COLORS.PREVIOUS
      });
    }

    this.barChartColors = colorConfigs;

    if (hasCompareData && compareData) {
      const concentricData = createConcentricDonutDatasets(
        formattedLabels,
        values,
        formattedLabels,
        compareData,
        Array.from(ANALYTICS_COLORS.PRODUCT)
      );
      this.productBreakdownDonutLabels = concentricData.labels;
      this.productBreakdownDonutData = concentricData.datasets;
    } else {
      const transformed = createSingleDonutDataset(
        formattedLabels,
        values,
        Array.from(ANALYTICS_COLORS.PRODUCT)
      );
      this.productBreakdownDonutLabels = transformed.labels;
      this.productBreakdownDonutData = transformed.datasets;
    }

    const hasCompare = this.filters.compareEnabled && this.productBreakdownDonutData.length > 1;
    this.productBreakdownDonutChartOptions = getDoughnutChartOptions(hasCompare);

    this.forceUpdateConsumptionChart();
    this.recreateProductBreakdownDonutChart();
  }

  private handleStackedConsumptionResponse(data: Record<string, unknown>): void {
    const labels = (data.labels as string[]) || [];
    const datasets = (data.datasets as Array<Record<string, unknown>>) || [];
    const compareDataObj = (data.compare as Record<string, unknown>) || (data.compareData as Record<string, unknown>) || undefined;
    const compareDatasets = compareDataObj && (compareDataObj.datasets as Array<Record<string, unknown>>) || undefined;

    this.consumptionBarChartLabels = labels.slice();

    // Populate contextual data from API response
    const contextualData = this.extractContextualData(data);
    if (contextualData) {
      this.populateContextualData(labels.slice(), contextualData);
    }

    const hasCompareData = this.filters.compareEnabled &&
                          compareDatasets &&
                          Array.isArray(compareDatasets) &&
                          compareDatasets.length > 0;

    const compareDatasetLookup: { [label: string]: number[] } = {};
    if (hasCompareData && compareDatasets) {
      for (let ci = 0; ci < compareDatasets.length; ci++) {
        const compareDataset = compareDatasets[ci];
        const compareLabelRaw = (compareDataset.label as string) || '';
        const formattedCompareLabel = formatProductName(compareLabelRaw);
        const compareValues = (compareDataset.data as number[]) || [];
        compareDatasetLookup[formattedCompareLabel] = compareValues.slice();
      }
    }

    const combinedDatasets: ChartDataset[] = [];
    const combinedColors: ChartColorConfig[] = [];
    this.consumptionCompareDataArray = [];

    for (let di = 0; di < datasets.length; di++) {
      const dataset = datasets[di];
      const productData = (dataset.data as number[]) || [];
      const productLabel = (dataset.label as string) || `Product ${di + 1}`;
      const formattedProductLabel = formatProductName(productLabel);
      const colorIndex = di % ANALYTICS_COLORS.PRODUCT.length;
      const color = ANALYTICS_COLORS.PRODUCT[colorIndex];

      combinedDatasets.push({
        label: formattedProductLabel,
        data: productData.slice(),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        stack: 'current'
      });
      combinedColors.push({
        backgroundColor: color,
        borderColor: color
      });

      if (hasCompareData) {
        const compareValues = compareDatasetLookup[formattedProductLabel] || [];
        const alignedCompareValues = this.consumptionBarChartLabels.map((_, labelIndex) => {
          if (!compareValues || !Array.isArray(compareValues)) {
            return 0;
          }
          return compareValues[labelIndex] || 0;
        });
        const compareColor = lightenColor(color, 0.35);

        combinedDatasets.push({
          label: formattedProductLabel + ' (Previous)',
          data: alignedCompareValues,
          backgroundColor: compareColor,
          borderColor: compareColor,
          borderWidth: 0,
          stack: 'previous'
        });
        combinedColors.push({
          backgroundColor: compareColor,
          borderColor: compareColor
        });
      }
    }

    this.consumptionBarChartData = combinedDatasets;

    // Store original unsorted data
    this.originalConsumptionBarChartLabels = labels.slice();
    this.originalConsumptionBarChartData = combinedDatasets.map(dataset => ({
      ...dataset,
      data: (dataset.data as number[]).slice()
    }));
    this.originalConsumptionCompareDataArray = [];

    this.sortConsumptionBarChart();
    this.barChartColors = combinedColors;

    const productLabels: string[] = [];
    const productTotals: number[] = [];

    for (let pi = 0; pi < datasets.length; pi++) {
      const dataset = datasets[pi];
      const productLabel = (dataset.label as string) || '';
      const formattedProductLabel = formatProductName(productLabel);
      const productData = (dataset.data as number[]) || [];
      const total = productData.reduce((sum, value) => sum + (value || 0), 0);
      productLabels.push(formattedProductLabel);
      productTotals.push(total);
    }

    const compareTotals: number[] = [];
    if (hasCompareData) {
      for (let ci = 0; ci < productLabels.length; ci++) {
        const label = productLabels[ci];
        const compareValues = compareDatasetLookup[label] || [];
        const compareTotal = compareValues.reduce((sum, value) => sum + (value || 0), 0);
        compareTotals.push(compareTotal);
      }
    }

    if (hasCompareData && compareTotals.length === productTotals.length) {
      const concentricData = createConcentricDonutDatasets(
        productLabels,
        productTotals,
        productLabels,
        compareTotals,
        Array.from(ANALYTICS_COLORS.PRODUCT)
      );
      this.productBreakdownDonutLabels = concentricData.labels;
      this.productBreakdownDonutData = concentricData.datasets;
    } else {
      const transformed = createSingleDonutDataset(
        productLabels,
        productTotals,
        Array.from(ANALYTICS_COLORS.PRODUCT)
      );
      this.productBreakdownDonutLabels = transformed.labels;
      this.productBreakdownDonutData = transformed.datasets;
    }

    const hasCompare = this.filters.compareEnabled && this.productBreakdownDonutData.length > 1;
    this.productBreakdownDonutChartOptions = getDoughnutChartOptions(hasCompare);

    this.forceUpdateConsumptionChart();
    this.recreateProductBreakdownDonutChart();
  }

  private recreateProductBreakdownDonutChart(): void {
    this.isDestroyingCharts = true;

    if (this.productBreakdownDonutChart && this.productBreakdownDonutChart.chart) {
      this.productBreakdownDonutChart.chart.destroy();
      (this.productBreakdownDonutChart as any).chart = null;
    }

    this.productBreakdownDonutChartKey = 0;
    this.cdr.markForCheck();

    setTimeout(() => {
      this.productBreakdownDonutChartKey++;
      this.cdr.markForCheck();
      this.isDestroyingCharts = false;
      setTimeout(() => {
        this.chartUpdateService.updateDonutChart(
          this.cdr,
          {
            chartInstance: this.productBreakdownDonutChart,
            chartKey: this.productBreakdownDonutChartKey,
            labels: this.productBreakdownDonutLabels,
            datasets: this.productBreakdownDonutData,
            chartOptions: this.productBreakdownDonutChartOptions,
            chartId: 'productBreakdownDonutChart',
            activeTab: 'consumption',
            requiredTab: 'consumption',
            isDestroyingCharts: this.isDestroyingCharts
          }
        );
      }, CHART_UPDATE_DELAY);
    }, 50);
  }

  loadConsumptionOverTimeData(): void {
    this.loadData(
      () => this.isLoadingConsumptionOverTime,
      (value) => { this.isLoadingConsumptionOverTime = value; },
      (params) => this.analyticsHttpService.getConsumptionOverTime(params),
      (response) => this.handleConsumptionOverTimeResponse(response),
      () => {
        this.consumptionLineChartLabels = [];
        this.consumptionLineChartData = [];
        this.forceUpdateConsumptionLineChart();
      },
      (params) => {
        delete params.breakdownBy;
        if (this.filters.averageEnabled && this.filters.avgBy) {
          params.avgBy = this.filters.avgBy;
        } else {
          params.avgBy = 'day';
        }
        if (this.filters.compareEnabled) {
          params.compare = 'true';
        }
      }
    );
  }

  private handleConsumptionOverTimeResponse(response: unknown): void {
    const data = this.parseApiResponse(response);
    if (!data) {
      this.consumptionLineChartLabels = [];
      this.consumptionLineChartData = [];
      this.forceUpdateConsumptionLineChart();
      return;
    }

    const labels = (data.labels as string[]) || [];
    const datasets = (data.datasets as Array<Record<string, unknown>>) || [];
    const compareData = data.compare as Record<string, unknown> | undefined;
    const compareLabels = compareData ? (compareData.labels as string[]) || [] : [];
    const dateRange = data.dateRange as { startDate?: string; endDate?: string } | undefined;
    const compareDateRange = data.compareDateRange as { startDate?: string; endDate?: string } | undefined;

    const chartLabelCount = labels.length;
    const shouldAlignCompare = !this.filters.averageEnabled || (this.filters.avgBy || 'day') === 'day';

    this.consumptionLineChartLabels = labels.slice();

    this.consumptionLineChartData = datasets.map((dataset, index) => {
      const productData = (dataset.data as number[]) || [];
      const productLabel = (dataset.label as string) || `Product ${index + 1}`;
      const formattedProductLabel = formatProductName(productLabel);
      const colorIndex = index % ANALYTICS_COLORS.PRODUCT.length;
      const color = ANALYTICS_COLORS.PRODUCT[colorIndex];

      return {
        label: formattedProductLabel,
        data: productData.slice(),
        borderColor: color,
        backgroundColor: `rgba(${hexToRgb(color)}, 0.1)`,
        pointBackgroundColor: color,
        pointBorderColor: color,
        fill: false,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
        lineTension: 0.4
      };
    });

    if (this.filters.compareEnabled && compareData) {
      const compareDatasets = (compareData.datasets as Array<Record<string, unknown>>) || [];
      compareDatasets.forEach((compareDataset, index) => {
        const productLabel = (compareDataset.label as string) || `Product ${index + 1}`;
        const formattedProductLabel = formatProductName(productLabel);
        const compareProductData = (compareDataset.data as number[]) || [];
        const compareValuesAdjusted = shouldAlignCompare
          ? this.alignSeriesWithOffset(
              compareProductData,
              this.getCompareAlignmentOffset(
                dateRange,
                compareDateRange,
                labels,
                compareLabels
              ),
              chartLabelCount
            )
          : this.normalizeSeriesLength(compareProductData, chartLabelCount);

        const matchingIndex = this.consumptionLineChartData.findIndex(ds => ds.label === formattedProductLabel);
        const colorIndex = matchingIndex >= 0 ? matchingIndex % ANALYTICS_COLORS.PRODUCT.length : index % ANALYTICS_COLORS.PRODUCT.length;
        const baseColor = ANALYTICS_COLORS.PRODUCT[colorIndex];
        const compareColor = lightenColor(baseColor, 0.3);

        this.consumptionLineChartData.push({
          label: `${formattedProductLabel} (Previous)`,
          data: compareValuesAdjusted,
          borderColor: compareColor,
          backgroundColor: `rgba(${hexToRgb(compareColor)}, 0.1)`,
          pointBackgroundColor: compareColor,
          pointBorderColor: compareColor,
          fill: false,
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 2,
          pointHoverRadius: 4,
          lineTension: 0.4
        });
      });

    }

    const trimmed = this.trimZeroOnlyPoints(this.consumptionLineChartLabels, this.consumptionLineChartData);
    this.consumptionLineChartLabels = trimmed.labels;
    this.consumptionLineChartData = trimmed.datasets;

    this.lineChartColors = this.consumptionLineChartData.map(dataset => ({
      borderColor: typeof dataset.borderColor === 'string' ? dataset.borderColor : ANALYTICS_COLORS.PRODUCT[0],
      backgroundColor: typeof dataset.backgroundColor === 'string' ? dataset.backgroundColor : 'rgba(59, 130, 246, 0.1)',
      pointBackgroundColor: typeof dataset.pointBackgroundColor === 'string' ? dataset.pointBackgroundColor : ANALYTICS_COLORS.PRODUCT[0],
      pointBorderColor: typeof dataset.pointBorderColor === 'string' ? dataset.pointBorderColor : ANALYTICS_COLORS.PRODUCT[0]
    }));

    this.forceUpdateConsumptionLineChart();
  }

  private forceUpdateConsumptionLineChart(): void {
    const tempLabels = this.consumptionLineChartLabels.slice();
    const tempDatasets = this.consumptionLineChartData.map(ds => ({ ...ds }));

    this.consumptionLineChartLabels = [];
    this.consumptionLineChartData = [];
    this.cdr.detectChanges();

    setTimeout(() => {
      this.consumptionLineChartLabels = tempLabels;
      this.consumptionLineChartData = tempDatasets;
      this.cdr.detectChanges();
    }, 10);
  }

  private getCompareAlignmentOffset(
    dateRange?: { startDate?: string; endDate?: string },
    compareDateRange?: { startDate?: string; endDate?: string },
    currentLabels?: string[],
    compareLabels?: string[]
  ): number {
    const rangeStart = dateRange && dateRange.startDate ? new Date(dateRange.startDate) : null;
    const compareRangeStart = compareDateRange && compareDateRange.startDate
      ? new Date(compareDateRange.startDate)
      : null;

    const rangeOffset = this.calculateDayOffset(rangeStart, compareRangeStart);
    if (rangeOffset !== null) {
      return rangeOffset;
    }

    const firstCurrentLabel = currentLabels && currentLabels.length > 0 ? currentLabels[0] : undefined;
    const firstCompareLabel = compareLabels && compareLabels.length > 0 ? compareLabels[0] : undefined;
    const currentLabelDate = this.parseLabelToDate(firstCurrentLabel);
    const compareLabelDate = this.parseLabelToDate(firstCompareLabel);

    const labelOffset = this.calculateDayOffset(currentLabelDate, compareLabelDate);
    return labelOffset !== null ? labelOffset : 0;
  }

  private calculateDayOffset(currentDate: Date | null, compareDate: Date | null): number | null {
    if (!currentDate || !compareDate || Number.isNaN(currentDate.getTime()) || Number.isNaN(compareDate.getTime())) {
      return null;
    }

    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    return Math.round((currentDate.getTime() - compareDate.getTime()) / MS_PER_DAY);
  }

  private alignSeriesWithOffset(series: number[], offset: number, targetLength: number): number[] {
    if (targetLength <= 0) {
      return [];
    }

    const aligned = new Array<number>(targetLength).fill(0);

    for (let i = 0; i < targetLength; i++) {
      const sourceIndex = i - offset;
      if (sourceIndex >= 0 && sourceIndex < series.length) {
        aligned[i] = series[sourceIndex];
      } else {
        aligned[i] = 0;
      }
    }

    return aligned;
  }

  private normalizeSeriesLength(series: number[], targetLength: number): number[] {
    if (targetLength <= 0) {
      return [];
    }
    if (series.length === targetLength) {
      return series.slice();
    }
    if (series.length > targetLength) {
      return series.slice(series.length - targetLength);
    }

    const result = new Array<number>(targetLength).fill(0);
    const startIndex = targetLength - series.length;
    for (let i = 0; i < series.length; i++) {
      result[startIndex + i] = series[i];
    }
    return result;
  }

  private parseLabelToDate(label?: string): Date | null {
    if (!label) {
      return null;
    }

    const parsed = new Date(label);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }

    const fallback = new Date(`${label} ${new Date().getFullYear()}`);
    if (!Number.isNaN(fallback.getTime())) {
      return fallback;
    }

    return null;
  }

  private trimZeroOnlyPoints(
    labels: string[],
    datasets: ChartDataset[]
  ): { labels: string[]; datasets: ChartDataset[] } {
    if (labels.length === 0 || datasets.length === 0) {
      return { labels, datasets };
    }

    let firstNonZeroIndex = -1;
    let lastNonZeroIndex = -1;

    for (let i = 0; i < labels.length; i++) {
      const hasValue = datasets.some(dataset => {
        const dataArray = dataset.data as number[] | undefined;
        if (!Array.isArray(dataArray) || dataArray.length <= i) {
          return false;
        }
        const value = dataArray[i];
        return typeof value === 'number' && value !== 0;
      });

      if (hasValue) {
        if (firstNonZeroIndex === -1) {
          firstNonZeroIndex = i;
        }
        lastNonZeroIndex = i;
      }
    }

    if (firstNonZeroIndex === -1 || lastNonZeroIndex === -1) {
      return {
        labels: [],
        datasets: datasets.map(dataset => ({ ...dataset, data: [] }))
      };
    }

    const trimmedLabels = labels.slice(firstNonZeroIndex, lastNonZeroIndex + 1);
    const trimmedDatasets = datasets.map(dataset => {
      const dataArray = Array.isArray(dataset.data) ? dataset.data as number[] : [];
      return {
        ...dataset,
        data: dataArray.slice(firstNonZeroIndex, lastNonZeroIndex + 1)
      };
    });

    return {
      labels: trimmedLabels,
      datasets: trimmedDatasets
    };
  }


  toggleConsumptionBarChartSort(): void {
    this.consumptionBarChartSort = this.consumptionBarChartSort === 'desc' ? 'asc' : 'desc';
    this.sortConsumptionBarChart();
    this.forceUpdateConsumptionChart();
  }

  private sortConsumptionBarChart(): void {
    const isStacked = this.filters.breakdownBy !== 'product';

    const labelsToSort = this.originalConsumptionBarChartLabels.length > 0
      ? this.originalConsumptionBarChartLabels
      : this.consumptionBarChartLabels;
    const dataToSort = this.originalConsumptionBarChartData.length > 0
      ? this.originalConsumptionBarChartData
      : this.consumptionBarChartData;
    const compareDataToSort = this.originalConsumptionCompareDataArray.length > 0
      ? this.originalConsumptionCompareDataArray
      : this.consumptionCompareDataArray;

    if (isStacked) {
      const result = sortStackedBarChartData(
        labelsToSort,
        dataToSort,
        this.consumptionBarChartSort
      );
      this.fullConsumptionBarChartLabels = result.labels;
      this.fullConsumptionBarChartData = result.datasets;
      this.fullConsumptionCompareDataArray = [];
    } else {
      const result = sortBarChartData(
        labelsToSort,
        dataToSort,
        compareDataToSort.length > 0 ? compareDataToSort : undefined,
        this.consumptionBarChartSort,
        this.filters.compareEnabled
      );
      this.fullConsumptionBarChartLabels = result.labels;
      this.fullConsumptionBarChartData = result.datasets;
      this.fullConsumptionCompareDataArray = result.compareData;
    }

    this.updateConsumptionDisplayData();
  }

  private updateConsumptionDisplayData(): void {
    const limit = this.showAllConsumptionItems
      ? this.fullConsumptionBarChartLabels.length
      : DEFAULT_CHART_ITEM_LIMIT;

    const result = limitBarChartData(
      this.fullConsumptionBarChartLabels,
      this.fullConsumptionBarChartData,
      this.fullConsumptionCompareDataArray.length > 0 ? this.fullConsumptionCompareDataArray : undefined,
      limit,
      false
    );

    this.consumptionBarChartLabels = result.labels;
    this.consumptionBarChartData = result.datasets;
    this.consumptionCompareDataArray = result.compareData;
  }

  toggleShowAllConsumptionItems(): void {
    this.showAllConsumptionItems = !this.showAllConsumptionItems;
    this.updateConsumptionDisplayData();
    this.forceUpdateConsumptionChart();
  }

  private forceUpdateConsumptionChart(): void {
    this.chartUpdateService.recreateBarChart(
      this.cdr,
      () => ({
        labels: this.consumptionBarChartLabels,
        datasets: this.consumptionBarChartData,
        compareData: this.consumptionCompareDataArray
      }),
      (data) => {
        this.consumptionBarChartLabels = data.labels;
        this.consumptionBarChartData = data.datasets;
        this.consumptionCompareDataArray = data.compareData;
      },
      'ConsumptionBarChart'
    );
  }

  shouldShowConsumptionToggle(): boolean {
    return this.fullConsumptionBarChartLabels.length > DEFAULT_CHART_ITEM_LIMIT;
  }

  getConsumptionToggleLabel(): string {
    if (this.showAllConsumptionItems) {
      return this.consumptionBarChartSort === 'desc' ? 'Top 10' : 'Bottom 10';
    }
    return 'All';
  }

  getConsumptionToggleTitle(): string {
    if (this.showAllConsumptionItems) {
      return this.consumptionBarChartSort === 'desc' ? 'Show top 10' : 'Show bottom 10';
    }
    return 'Show all';
  }
  getBarChartOptions(stacked: boolean): unknown {
    return stacked ? this.stackedBarChartOptions : this.barChartOptions;
  }

  getConsumptionBarChartTotal(): number {
    return calculateFirstDatasetTotal(this.consumptionBarChartData);
  }

  getConsumptionValue(index: number): number {
    if (!this.consumptionBarChartData || this.consumptionBarChartData.length === 0) {
      return 0;
    }
    return getDatasetValue(this.consumptionBarChartData[0], index);
  }

  isConsumptionStacked(): boolean {
    return this.filters.breakdownBy !== 'product';
  }

  hasConsumptionCompareData(): boolean {
    return this.filters.compareEnabled &&
           this.consumptionCompareDataArray &&
           this.consumptionCompareDataArray.length > 0;
  }

  getConsumptionCompareValue(index: number): number {
    return getArrayValue(this.consumptionCompareDataArray, index);
  }

  getConsumptionCompareTotal(): number {
    return calculateArrayTotal(this.consumptionCompareDataArray);
  }

  getConsumptionValueForProduct(productIndex: number, labelIndex: number): number {
    return getProductValue(this.consumptionBarChartData, productIndex, labelIndex);
  }

  getConsumptionTotalForProduct(productIndex: number): number {
    return getProductTotal(this.consumptionBarChartData, productIndex);
  }

  getBreakdownDisplayName(option: string): string {
    return getBreakdownDisplayNameUtil(option);
  }

  hasInsufficientTimeseriesData(labels: string[]): boolean {
    return labels.length === 1;
  }

  hasNoData(labels: string[], datasets: ChartDataset[]): boolean {
    return labels.length === 0 || datasets.length === 0 || (datasets.length > 0 && datasets[0].data.length === 0);
  }

  /**
   * Get tooltip text for a label based on breakdown type
   */
  getContextualTooltip(label: string): string {
    const breakdownBy = this.filters.breakdownBy;
    const context = this.contextualDataMap.get(label);

    if (!context) {
      return '';
    }

    if (breakdownBy === 'client') {
      // Show Partner
      return context.partner ? `Partner: ${context.partner}` : '';
    } else if (breakdownBy === 'campus') {
      // Show Partner first, then Client
      const parts: string[] = [];
      if (context.partner) {
        parts.push(`Partner: ${context.partner}`);
      }
      if (context.client) {
        parts.push(`Client: ${context.client}`);
      }
      return parts.join('\n');
    } else if (breakdownBy === 'machine') {
      // Show Partner first, then Client, then Campus
      const parts: string[] = [];
      if (context.partner) {
        parts.push(`Partner: ${context.partner}`);
      }
      if (context.client) {
        parts.push(`Client: ${context.client}`);
      }
      if (context.campus) {
        parts.push(`Campus: ${context.campus}`);
      }
      return parts.join('\n');
    }

    return '';
  }

  /**
   * Check if tooltip should be shown based on breakdown type
   */
  shouldShowContextualTooltip(): boolean {
    const breakdownBy = this.filters.breakdownBy;
    return breakdownBy === 'client' || breakdownBy === 'campus' || breakdownBy === 'machine';
  }

  /**
   * Show custom tooltip on mouse enter
   */
  onTooltipMouseEnter(event: MouseEvent, label: string): void {
    if (!this.shouldShowContextualTooltip()) {
      return;
    }
    const tooltipText = this.getContextualTooltip(label);
    if (!tooltipText) {
      return;
    }
    this.tooltipVisible = true;
    this.tooltipText = tooltipText;
    // Set position once on enter - tooltip stays static
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY - 10; // 10px above cursor
  }

  /**
   * Hide tooltip on mouse leave
   */
  onTooltipMouseLeave(): void {
    this.tooltipVisible = false;
  }

  /**
   * Populate contextual data from API response
   */
  private populateContextualData(labels: string[], contextualData: Record<string, { partner?: string; client?: string; campus?: string }>): void {
    const breakdownBy = this.filters.breakdownBy;

    // Only populate for client, campus, or machine breakdowns
    if (breakdownBy !== 'client' && breakdownBy !== 'campus' && breakdownBy !== 'machine') {
      return;
    }

    // Clear existing contextual data for these labels
    labels.forEach(label => {
      this.contextualDataMap.delete(label);
    });

    // Populate from API response
    labels.forEach((label) => {
      const contextData = contextualData[label];
      if (contextData) {
        const context: { partner?: string; client?: string; campus?: string } = {};

        if (contextData.partner) {
          context.partner = contextData.partner;
        }
        if (contextData.client) {
          context.client = contextData.client;
        }
        if (contextData.campus) {
          context.campus = contextData.campus;
        }

        // Only set if we have at least one property
        if (context.partner || context.client || context.campus) {
          this.contextualDataMap.set(label, context);
        }
      }
    });
  }

  /**
   * Check if data is available for export
   * Check both full and limited arrays to handle all breakdown scenarios
   */
  hasData(): boolean {
    // Check limited arrays (what's currently displayed)
    const hasLimitedData = this.consumptionBarChartLabels.length > 0 &&
           this.consumptionBarChartData.length > 0 &&
           (this.consumptionBarChartData[0] && this.consumptionBarChartData[0].data && this.consumptionBarChartData[0].data.length > 0);

    // Check full arrays (all data, including when "show all" is available)
    const hasFullData = this.fullConsumptionBarChartLabels.length > 0 &&
           this.fullConsumptionBarChartData.length > 0 &&
           (this.fullConsumptionBarChartData[0] && this.fullConsumptionBarChartData[0].data && this.fullConsumptionBarChartData[0].data.length > 0);

    return hasLimitedData || hasFullData;
  }

  /**
   * Export consumption table to Excel
   */
  exportTable(): void {
    try {
      // Use full arrays for export (all data, not just visible)
      const labels = this.fullConsumptionBarChartLabels.length > 0
        ? this.fullConsumptionBarChartLabels
        : this.consumptionBarChartLabels;

      // Safely extract data, handling single-row cases
      let data: ChartDataset[] = [];
      if (this.fullConsumptionBarChartData.length > 0) {
        data = this.fullConsumptionBarChartData;
      } else if (this.consumptionBarChartData.length > 0) {
        data = this.consumptionBarChartData;
      }

      const compareData = this.filters.compareEnabled && this.fullConsumptionCompareDataArray.length > 0
        ? this.fullConsumptionCompareDataArray
        : (this.filters.compareEnabled && this.consumptionCompareDataArray.length > 0
           ? this.consumptionCompareDataArray
           : undefined);

      // Ensure labels and data arrays have matching lengths
      if (labels.length === 0 || data.length === 0) {
        console.warn('No data available for export');
        return;
      }

      const exportData = {
        labels: labels,
        data: data,
        compareData: compareData,
        isStacked: this.isConsumptionStacked(),
        breakdownBy: this.filters.breakdownBy,
        total: this.getConsumptionBarChartTotal(),
        compareTotal: this.filters.compareEnabled ? this.getConsumptionCompareTotal() : undefined
      };

      this.analyticsExportService.exportConsumptionTable(
        exportData,
        this.filters,
        'Consumption',
        this.drinkOptions,
        this.productOptions,
        this.distributorOptions,
        this.marketOptions,
        this.clientOptions,
        this.campusOptions,
        this.machineOptions
      );
    } catch (error) {
      console.error('Error exporting consumption table:', error);
      throw error;
    }
  }
}


